import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Printer, Download, FileSpreadsheet, FileText } from 'lucide-react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';

const AccountsReport = () => {
  const [reportData, setReportData] = useState(null);

  useEffect(() => {
    const data = localStorage.getItem('accountsReportData');
    if (data) {
      setReportData(JSON.parse(data));
    }
  }, []);

  const formatCurrency = (amount) => {
    const num = parseFloat(amount) || 0;
    return num.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const handlePrint = () => {
    window.print();
  };

  const exportToPDF = () => {
    if (!reportData) return;

    const doc = new jsPDF('landscape');
    const pageWidth = doc.internal.pageSize.getWidth();
    
    // Title
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text('Accounts Transaction Report', pageWidth / 2, 15, { align: 'center' });
    
    // Filters applied
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    let filterText = 'Filters: ';
    if (reportData.filters.startDate) filterText += `From: ${reportData.filters.startDate} `;
    if (reportData.filters.endDate) filterText += `To: ${reportData.filters.endDate} `;
    if (reportData.filters.selectedLedger) filterText += `Ledger: ${reportData.filters.selectedLedger} `;
    if (reportData.filters.transactionTypeFilter !== 'all') filterText += `Type: ${reportData.filters.transactionTypeFilter}`;
    if (filterText === 'Filters: ') filterText = 'Filters: All Transactions';
    doc.text(filterText, 14, 25);

    // Summary
    doc.setFontSize(11);
    doc.text(`Total Transactions: ${reportData.summary.totalTransactions}`, 14, 33);
    doc.setTextColor(220, 38, 38);
    doc.text(`Total Debit: Rs. ${formatCurrency(reportData.summary.totalDebit)}`, 100, 33);
    doc.setTextColor(22, 163, 74);
    doc.text(`Total Credit: Rs. ${formatCurrency(reportData.summary.totalCredit)}`, 180, 33);
    doc.setTextColor(0, 0, 0);

    // Table
    const tableData = reportData.transactions.map(t => [
      t.date || '-',
      t.narration || '-',
      t.transaction_type || '-',
      `Rs. ${formatCurrency(t.amount)}`,
      `Rs. ${formatCurrency(t.balance)}`,
      t.ledger || '-'
    ]);

    autoTable(doc, {
      head: [['Date', 'Narration', 'Type', 'Amount', 'Balance', 'Ledger']],
      body: tableData,
      startY: 40,
      styles: { fontSize: 8, cellPadding: 2 },
      headStyles: { fillColor: [59, 130, 246], textColor: 255 },
      columnStyles: {
        0: { cellWidth: 25 },
        1: { cellWidth: 80 },
        2: { cellWidth: 20 },
        3: { cellWidth: 30, halign: 'right' },
        4: { cellWidth: 30, halign: 'right' },
        5: { cellWidth: 40 }
      },
      alternateRowStyles: { fillColor: [248, 250, 252] }
    });

    doc.save('accounts_report.pdf');
  };

  const exportToExcel = () => {
    if (!reportData) return;

    const worksheetData = [
      ['Accounts Transaction Report'],
      [],
      ['Filters Applied:'],
      [`From: ${reportData.filters.startDate || 'All'}`, `To: ${reportData.filters.endDate || 'All'}`, `Ledger: ${reportData.filters.selectedLedger || 'All'}`, `Type: ${reportData.filters.transactionTypeFilter || 'All'}`],
      [],
      ['Summary:'],
      [`Total Transactions: ${reportData.summary.totalTransactions}`, `Total Debit: ${formatCurrency(reportData.summary.totalDebit)}`, `Total Credit: ${formatCurrency(reportData.summary.totalCredit)}`],
      [],
      ['Date', 'Narration', 'Type', 'Amount', 'Balance', 'Ledger'],
      ...reportData.transactions.map(t => [
        t.date || '',
        t.narration || '',
        t.transaction_type || '',
        parseFloat(t.amount) || 0,
        parseFloat(t.balance) || 0,
        t.ledger || ''
      ])
    ];

    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Transactions');
    XLSX.writeFile(workbook, 'accounts_report.xlsx');
  };

  if (!reportData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-slate-700">No Report Data</h2>
          <p className="text-slate-500 mt-2">Please apply filters and click "Print Report" from Accounts page.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header - Hidden when printing */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white p-4 no-print">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <h1 className="text-xl font-bold">Accounts Transaction Report</h1>
          <div className="flex gap-2">
            <Button onClick={handlePrint} className="bg-white/20 hover:bg-white/30 text-white">
              <Printer className="w-4 h-4 mr-2" />
              Print
            </Button>
            <Button onClick={exportToPDF} className="bg-red-500 hover:bg-red-600 text-white">
              <FileText className="w-4 h-4 mr-2" />
              Export PDF
            </Button>
            <Button onClick={exportToExcel} className="bg-green-500 hover:bg-green-600 text-white">
              <FileSpreadsheet className="w-4 h-4 mr-2" />
              Export Excel
            </Button>
          </div>
        </div>
      </div>

      {/* Report Content */}
      <div className="max-w-7xl mx-auto p-6">
        {/* Report Header for Print */}
        <div className="text-center mb-6 print-only hidden print:block">
          <h1 className="text-2xl font-bold text-slate-800">Accounts Transaction Report</h1>
          <p className="text-slate-500">Generated on {new Date().toLocaleDateString('en-IN')}</p>
        </div>

        {/* Filters Applied */}
        <div className="bg-slate-50 rounded-lg p-4 mb-6 border border-slate-200">
          <h3 className="font-semibold text-slate-700 mb-2">Filters Applied:</h3>
          <div className="flex flex-wrap gap-3">
            {reportData.filters.startDate && (
              <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm">
                From: {reportData.filters.startDate}
              </span>
            )}
            {reportData.filters.endDate && (
              <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm">
                To: {reportData.filters.endDate}
              </span>
            )}
            {reportData.filters.selectedLedger && (
              <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm">
                Ledger: {reportData.filters.selectedLedger}
              </span>
            )}
            {reportData.filters.transactionTypeFilter !== 'all' && (
              <span className={`px-3 py-1 rounded-full text-sm ${reportData.filters.transactionTypeFilter === 'Debit' ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                Type: {reportData.filters.transactionTypeFilter}
              </span>
            )}
            {!reportData.filters.startDate && !reportData.filters.endDate && !reportData.filters.selectedLedger && reportData.filters.transactionTypeFilter === 'all' && (
              <span className="px-3 py-1 bg-slate-200 text-slate-600 rounded-full text-sm">
                All Transactions (No filters)
              </span>
            )}
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg p-4 text-white">
            <p className="text-blue-100 text-sm">Total Transactions</p>
            <p className="text-2xl font-bold">{reportData.summary.totalTransactions}</p>
          </div>
          <div className="bg-gradient-to-br from-red-500 to-red-600 rounded-lg p-4 text-white">
            <p className="text-red-100 text-sm">Total Debit</p>
            <p className="text-2xl font-bold">Rs. {formatCurrency(reportData.summary.totalDebit)}</p>
          </div>
          <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-lg p-4 text-white">
            <p className="text-green-100 text-sm">Total Credit</p>
            <p className="text-2xl font-bold">Rs. {formatCurrency(reportData.summary.totalCredit)}</p>
          </div>
        </div>

        {/* Transactions Table */}
        <div className="border border-slate-200 rounded-lg overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-100">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase">Date</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase">Narration</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-slate-700 uppercase">Type</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-slate-700 uppercase">Amount</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-slate-700 uppercase">Balance</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase">Ledger</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {reportData.transactions.map((t, idx) => (
                <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                  <td className="px-4 py-2 text-sm text-slate-700">{t.date || '-'}</td>
                  <td className="px-4 py-2 text-sm text-slate-700">{t.narration || '-'}</td>
                  <td className="px-4 py-2 text-center">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${t.transaction_type === 'Debit' ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                      {t.transaction_type || '-'}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-sm text-right font-medium text-slate-800">Rs. {formatCurrency(t.amount)}</td>
                  <td className="px-4 py-2 text-sm text-right text-slate-600">Rs. {formatCurrency(t.balance)}</td>
                  <td className="px-4 py-2 text-sm text-slate-600">{t.ledger || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Footer for Print */}
        <div className="mt-6 text-center text-slate-500 text-sm print-only hidden print:block">
          <p>Report generated from Stock Tracking System</p>
        </div>
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          .no-print { display: none !important; }
          .print-only { display: block !important; }
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
      `}</style>
    </div>
  );
};

export default AccountsReport;
